import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { SessionPriceTier } from '../types/firestoreTypes';
import { useTranslation } from 'react-i18next';

type PriceTierSaveData = Pick<
  SessionPriceTier,
  'tierName' | 'price' | 'currency' | 'durationMinutes'
>;

interface AddEditPriceTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PriceTierSaveData) => void;
  initialData?: SessionPriceTier | null;
}

interface FormData {
  tierName: string;
  price: string;
  currency: string;
  durationMinutes: string;
}

const AddEditPriceTierModal: React.FC<AddEditPriceTierModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    tierName: '',
    price: '',
    currency: 'EUR',
    durationMinutes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        tierName: initialData.tierName,
        price: initialData.price.toString(),
        currency: initialData.currency,
        durationMinutes: initialData.durationMinutes.toString(),
      });
    }
  }, [initialData]);

  const validate = () => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.tierName)
      newErrors.tierName = t('priceTier.errors.nameRequired');
    if (!formData.price) newErrors.price = t('priceTier.errors.priceRequired');
    else if (parseFloat(formData.price) <= 0)
      newErrors.price = t('priceTier.errors.priceMin');
    if (!formData.currency)
      newErrors.currency = t('priceTier.errors.currencyRequired');
    if (!formData.durationMinutes)
      newErrors.durationMinutes = t('priceTier.errors.durationRequired');
    else if (parseInt(formData.durationMinutes) <= 0)
      newErrors.durationMinutes = t('priceTier.errors.durationMin');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: undefined });
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setIsSubmitting(true);
    const data: PriceTierSaveData = {
      tierName: formData.tierName,
      price: parseFloat(formData.price),
      currency: formData.currency,
      durationMinutes: parseInt(formData.durationMinutes),
    };
    onSave(data);
    setIsSubmitting(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        initialData
          ? t('priceTier.actions.edit')
          : t('priceTier.actions.addNew')
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#344054] mb-1.5">
            {t('priceTier.form.tierName')}
          </label>
          <Input
            value={formData.tierName}
            onChange={(e) => handleChange('tierName', e.target.value)}
          />
          {errors.tierName && (
            <p className="mt-1 text-sm text-red-500">{errors.tierName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#344054] mb-1.5">
            {t('priceTier.form.price')}
          </label>
          <Input
            type="number"
            value={formData.price}
            onChange={(e) => handleChange('price', e.target.value)}
            step="0.01"
          />
          {errors.price && (
            <p className="mt-1 text-sm text-red-500">{errors.price}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#344054] mb-1.5">
            {t('priceTier.form.currency')}
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            value={formData.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
          >
            <option value="EUR">EUR</option>
            <option value="HUF">HUF</option>
          </select>
          {errors.currency && (
            <p className="mt-1 text-sm text-red-500">{errors.currency}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#344054] mb-1.5">
            {t('priceTier.form.duration')}
          </label>
          <Input
            type="number"
            value={formData.durationMinutes}
            onChange={(e) => handleChange('durationMinutes', e.target.value)}
          />
          {errors.durationMinutes && (
            <p className="mt-1 text-sm text-red-500">
              {errors.durationMinutes}
            </p>
          )}
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={isSubmitting} type="button">
            {t('priceTier.actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditPriceTierModal;
